import {
  hashKey,
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { parse } from 'graphql';
import { shouldRedirectOnLogout } from '@/components/auth';
import { ErrorCodes } from '@/types/gql.generated';
import {
  getGQLErrorCode,
  getReportableServerMessage,
  type GraphQLError,
  isGQLError,
  isGQLErrorOfType,
  type QueryError,
} from '@/utils/errors';
import {
  createProfileQueryKey,
  createSnippetQueryKey,
} from '@/utils/queryKeys';
import { logger } from './logger';
import { toReportableQueryKey } from './redactSensitiveData';
import { Sentry } from './sentry';

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: QueryError;
  }
}

// Gateway/timeout statuses that mean the request never reached (or timed out
// against) our application: a proxy/CDN outage, a rolling deploy, or a
// momentarily unavailable origin (504 gateway timeout, Cloudflare 52x). Nothing
// in the client can fix them and the next request usually succeeds, so — like
// the transient PDF and chunk-load failures we already tolerate — they're noise
// in the frontend project rather than an actionable defect. A genuine
// application-level 500/501 (the GraphQL server itself responded with an error)
// is still reported (Sentry WEB-CLIENT-1HG / WEB-CLIENT-25E / WEB-CLIENT-279 /
// WEB-CLIENT-25G).
//
// 521 ("Web Server Is Down" — Cloudflare's TCP connection to the origin was
// refused) is the same class as its 522/523/524 neighbours and was simply
// missed when this set was first written. One brief origin outage then minted
// four separate issues, one per in-flight operation, because the 5xx path
// fingerprints by operation name (Sentry WEB-CLIENT-27T / WEB-CLIENT-27V /
// WEB-CLIENT-27W / WEB-CLIENT-27Z). The set stays evidence-driven rather than
// covering all of 520-527: only statuses actually observed in production are
// suppressed, so an unfamiliar edge failure still surfaces once.
//
// 520 ("Web Server Returned an Unknown Error" — Cloudflare's catch-all for an
// origin response it can't otherwise classify) is the same edge-outage class,
// observed once against a single query with zero user impact (WEB-CLIENT-28P).
const TRANSIENT_GATEWAY_STATUSES = new Set([
  502, 503, 504, 520, 521, 522, 523, 524,
]);

/**
 * A GraphQL request that failed with a transient gateway/timeout status — the
 * request didn't reach our application, so there's nothing in our code to fix.
 */
export const isTransientGatewayError = (error: unknown): boolean =>
  isGQLError(error) && TRANSIENT_GATEWAY_STATUSES.has(error.response.status);

/** A 4xx: the server understood the request and refused it. */
const isClientErrorStatus = (status: number): boolean =>
  status >= 400 && status < 500;

/**
 * The name of the GraphQL operation a failed request was running, e.g.
 * `GetPublishedPageExperience`. `undefined` for an anonymous operation or a
 * document we can't parse.
 *
 * Parsed rather than pattern-matched: our documents carry `#` comments, and a
 * commented-out operation ahead of the real one would otherwise win and file
 * the error under the wrong name. `parse` is already in the bundle — it's how
 * graphql-request analyses each document before sending it.
 */
export const getGQLOperationName = (
  error: GraphQLError
): string | undefined => {
  const { query } = error.request;
  const document = Array.isArray(query) ? query.join('\n') : query;
  if (!document) {
    return undefined;
  }

  try {
    const { definitions } = parse(document);
    const operation = definitions.find(
      (definition) => definition.kind === 'OperationDefinition'
    );
    return operation?.name?.value;
  } catch {
    // An unparseable document tells us nothing about the operation, but the
    // 5xx it failed with is still worth reporting.
    return undefined;
  }
};

/**
 * Tears down the client's view of the signed-in user: the same cache work
 * `useLogout` does once its mutation succeeds, minus the mutation itself (a
 * dead session has nothing to sign out of) and minus the navigation, which
 * each caller does with whatever it has to hand.
 *
 * No-ops unless the profile cache currently holds a user. That does double
 * duty. Anonymous visitors on public pages do fire authed queries, and clearing
 * the cache under them would discard the page they came to read and stampede a
 * refetch of it. It also bounds the recursion: clearing makes every mounted
 * query refetch at once, each of those answers 401 against the dead session and
 * lands back here, and by then the profile is gone so they all return here.
 */
export const endSession = () => {
  const cached = queryClient.getQueryData<{ getProfile: unknown }>(
    createProfileQueryKey()
  );
  if (!cached?.getProfile) {
    return;
  }

  // Written before the clear that erases it: the write notifies observers
  // synchronously, so `isAuthenticated` flips false before the tree re-renders
  // rather than a tick later.
  queryClient.setQueriesData(
    { queryKey: createProfileQueryKey() },
    { getProfile: null }
  );
  queryClient.clear();
};

/**
 * The parts of a failed GraphQL request that are safe to send to Sentry.
 *
 * Everything here is read field-by-field rather than off the error itself,
 * because a `ClientError`'s message is the serialized request — `variables`
 * included, which on the auth mutations is the user's plaintext password. So no
 * capture on this path may pass the `ClientError` to Sentry or chain it as
 * `cause`, which the linked-errors integration would walk. `scrubSentryEvent`
 * backstops the same hazard for call sites that cannot avoid it.
 */
const safeGQLErrorContext = (error: GraphQLError, queryKey?: QueryKey) => ({
  tags: {
    graphqlOperation: getGQLOperationName(error) ?? 'unknown-operation',
    graphqlCode: getGQLErrorCode(error) ?? 'unknown-code',
  },
  extra: {
    queryKey: toReportableQueryKey(queryKey),
    serverMessage: getReportableServerMessage(error),
  },
});

/**
 * Codes where a 400 is the server answering *about the credentials the user
 * typed*, not rejecting a payload we built wrong. Neither is knowable client
 * side — no amount of local validation can tell whether a password matches or
 * an email is already registered — so the round trip is the only way to find
 * out, and the answer is rendered to the user by `getAuthErrorMessage`. Filing
 * an exception per mistyped password says nothing about our code and buries the
 * 400s that are genuinely our bug (Sentry WEB-CLIENT-28N).
 *
 * `MER_VALIDATION_ERR_FAILED_VALIDATION` is deliberately absent: the client does
 * validate email shape, so the server disagreeing means our own validation is
 * out of step with the server's, which is worth knowing.
 */
const USER_CREDENTIAL_CODES: ReadonlySet<string> = new Set([
  ErrorCodes.InvalidLogin,
  ErrorCodes.UserExists,
  // Same reasoning as InvalidLogin: whether a typed login code still matches
  // what was emailed, or hasn't expired, is knowable only by asking the
  // server. A mistyped or stale code says nothing about our code (Sentry
  // WEB-CLIENT-2AD / WEB-CLIENT-2AJ).
  ErrorCodes.VerificationCodeInvalid,
  ErrorCodes.VerificationCodeExpired,
]);

/**
 * Codes where a 400 is the server answering *about the password-reset token in
 * the URL*, not rejecting a payload we built wrong. A reset link can be
 * reused, mistyped by an email client, or simply gone stale by the time its
 * owner clicks it, and nothing client side can tell which before asking the
 * server — the round trip is the point, and `ResetPassword`'s own
 * `getErrorMessage` already renders a message specific to each case. Filing an
 * exception per stale link is the same problem `USER_CREDENTIAL_CODES` exists
 * to avoid, just for a token instead of a password (Sentry WEB-CLIENT-2AP).
 */
const RESET_TOKEN_CODES: ReadonlySet<string> = new Set([
  ErrorCodes.ResetTokenInvalid,
  ErrorCodes.ResetTokenExpired,
  ErrorCodes.ResetTokenUsed,
]);

/**
 * Mercurius answers `MER_ERR_GQL_PERSISTED_QUERY_NOT_FOUND` ("Unknown query")
 * for any request body it parsed that carries no `query`. The name is a
 * misnomer inherited from its persisted-query support, which we don't enable —
 * `mercurius/lib/routes.js` returns this code from a bare `if (!query)` before
 * persisted queries are ever consulted. Reproduced against production with
 * `{}`, with `{"query":""}`, and with a perfectly good JSON body relabelled
 * `application/x-www-form-urlencoded`, which `@fastify/formbody` parses into a
 * single junk key — so the query goes missing without the body being empty.
 *
 * It says nothing about which operation was in flight. graphql-request builds a
 * request's `query` from the same document it reports on the resulting error, so
 * a report that names its operation proves we sent that document and something
 * between our `fetch` and the server dropped it. That takes out every request in
 * the session at once, which is why one page load minted an issue per in-flight
 * operation (Sentry WEB-CLIENT-28Q / 28R / 28S / 28T / 28V). Grouped on the code
 * alone for the same reason the burst detector keys on it.
 */
const NO_QUERY_RECEIVED_CODE = 'MER_ERR_GQL_PERSISTED_QUERY_NOT_FOUND';

/**
 * The client asking for something it should not have. Two shapes reach here,
 * both of them our bug even though nothing is broken for the user yet:
 *
 * - a **400** (or a non-5xx `UnknownError`, which used to be captured as the raw
 *   `ClientError` — the same leak in a quieter place): we sent the server a
 *   payload it cannot accept. The client validates its own inputs, so a payload
 *   the server rejects should never have been built.
 * - a **403 on a mutation**: the app offered the user an action the server
 *   refuses, so the UI should not have presented it. This is the shape the
 *   magic-chat `importChat` denials took.
 *
 * A 403 on a *query* is deliberately absent: fanning several authed queries out
 * on a navigation is how every route loads, and the loaders already render an
 * access-denied state for it. Those are logged only — see `logClientError`.
 */
const reportClientFault = (error: GraphQLError, queryKey?: QueryKey) => {
  const { status } = error.response;
  const operationName = getGQLOperationName(error);
  const code = getGQLErrorCode(error);

  if (
    code &&
    (USER_CREDENTIAL_CODES.has(code) || RESET_TOKEN_CODES.has(code))
  ) {
    return;
  }

  const noQueryReceived = String(code) === NO_QUERY_RECEIVED_CODE;

  // "sent" for a payload we should never have built, "denied" for an action the
  // server refused — the same report, worded accurately for each.
  const summary =
    status === 403
      ? `${status} denied by the GraphQL API`
      : `${status} sent to the GraphQL API`;

  // The operation is left out of the message for a dropped query so the issue
  // title stays stable across whichever request happened to land first; it is
  // still on the event as `graphqlOperation`.
  const wrappedErr = new Error(
    noQueryReceived
      ? `${status} sent to the GraphQL API (no query reached the server)`
      : operationName
        ? `${summary} (${operationName})`
        : summary
  );

  Sentry.captureException(wrappedErr, {
    ...safeGQLErrorContext(error, queryKey),
    // Same reasoning as the 5xx fingerprint: left to group itself, one bad
    // input per page would mint a separate issue.
    fingerprint: noQueryReceived
      ? ['graphql-api-client-fault', NO_QUERY_RECEIVED_CODE]
      : [
          'graphql-api-client-fault',
          code ?? 'unknown-code',
          operationName ?? 'unknown-operation',
        ],
  });
};

/**
 * We asked for too much, too fast.
 *
 * Cloudflare enforces its rate limits at the edge and answers 429 there, so a
 * throttled request never reaches our server: nothing in the backend logs, and
 * nothing in the server's own Sentry project, records that it happened. The
 * client is the only place this is observable at all, and it used to be
 * swallowed here — a 429 is a 4xx, and the 4xx branch in `errorHandler` reports
 * only the shapes that are unambiguously our own bug. The first we heard of it
 * was a user reporting a broken `/dashboard`.
 *
 * It is always worth a report. Someone driving the UI normally should never
 * trip a rate limit; when they do, it means a screen fans out more requests
 * than we think it does, and the fix is ours even though no single request is
 * defective.
 *
 * Fingerprinted on the rate limit alone, deliberately *not* on the operation. A
 * burst takes out whichever requests happen to be in flight — the `/dashboard`
 * report was eight 429s inside one second — so grouping by operation would
 * split the one signal we want across an issue per query, the same way the
 * Cloudflare 52x reports once split one origin outage four ways (see
 * TRANSIENT_GATEWAY_STATUSES). The operation stays on the event as a tag, where
 * it can be faceted without fragmenting the issue.
 *
 * The burst itself is collapsed on the way out by `rateLimitSentryEvent`, so
 * the eight events above cost one report rather than eight.
 */
const reportRateLimited = (error: GraphQLError, queryKey?: QueryKey) => {
  const { headers } = error.response;
  const { tags, extra } = safeGQLErrorContext(error, queryKey);

  Sentry.captureException(new Error('429 rate limited by the GraphQL API'), {
    tags,
    extra: {
      ...extra,
      // Cloudflare's own error page is what comes back rather than a GraphQL
      // error envelope, so the diagnostics live in the response headers:
      // `retry-after` is how long the edge wants us to wait, and `cf-ray`
      // identifies the request in Cloudflare's firewall-event log, which is
      // where the rule that fired can actually be read. Both read `null` if the
      // 429 came from somewhere other than the edge, which is worth seeing too.
      retryAfter: headers?.get('retry-after'),
      cfRay: headers?.get('cf-ray'),
    },
    fingerprint: ['graphql-api-rate-limited'],
  });
};

/**
 * Every 4xx the GraphQL API answers, logged once per occurrence so Datadog can
 * show whether the client is asking for things the server refuses — which is
 * usually the first sign of a bug on our side.
 *
 * A log rather than a Sentry capture, because Sentry is the wrong shape for this
 * class. Most individual 4xx are legitimate answers: an authed route fans
 * several queries out at once, so a single signed-out or unauthorized visitor
 * answers 401/403 three or four times before the loader has rendered its
 * access-denied state. Grouped into an issue that reads as a defect nobody can
 * act on, and it buries the reports that are real (Sentry WEB-CLIENT-281 /
 * WEB-CLIENT-282). Datadog aggregates instead of grouping, so the *rate* is the
 * signal and a single occurrence costs nothing.
 *
 * Read field-by-field for the same reason `safeGQLErrorContext` is: a
 * `ClientError`'s own message is the serialized request, variables included,
 * which on the auth mutations is the user's plaintext password. Only these
 * vetted fields may leave the client.
 *
 * The message stays a fixed `GraphQL API <status>` so Datadog groups on it;
 * everything variable goes in attributes, where it can be faceted.
 */
const logClientError = (error: GraphQLError, queryKey?: QueryKey) => {
  const { status } = error.response;

  logger.warn(`GraphQL API ${status}`, {
    graphql: {
      status,
      code: getGQLErrorCode(error) ?? 'unknown-code',
      operation: getGQLOperationName(error) ?? 'unknown-operation',
      serverMessage: getReportableServerMessage(error),
      queryKey: toReportableQueryKey(queryKey),
      // Distinguishes "a navigation asked for something it can't have" from
      // "the app offered an action the server refuses" — the latter is the
      // shape the magic-chat `importChat` denials took.
      kind: queryKey === undefined ? 'mutation' : 'query',
    },
  });
};

const errorHandler = (error: unknown, queryKey?: QueryKey) => {
  const isGraphQLError = isGQLError(error);

  const data = {
    extra: {
      queryKey: toReportableQueryKey(queryKey),
    },
  };

  const isServerError = isGraphQLError && error.response.status >= 500;
  const isTransientGateway = isTransientGatewayError(error);
  // The query cache always passes a queryKey; the mutation cache never does.
  const isMutation = queryKey === undefined;

  if (isServerError && !isTransientGateway) {
    const { status } = error.response;
    const operationName = getGQLOperationName(error);
    const wrappedErr = new Error(
      operationName
        ? `${status} error received from the GraphQL API (${operationName})`
        : `${status} error received from the GraphQL API`
    );
    // The ClientError is deliberately NOT chained as `cause`. Its message is the
    // serialized request, variables included, and Sentry's linked-errors
    // integration walks the chain — so chaining it uploads the user's password
    // on any 5xx from an auth mutation. The diagnostics worth having are
    // extracted explicitly below instead.
    //
    // Not chaining also fixes the grouping the old comment here described: the
    // embedded page slugs and record ids used to hash into the default
    // fingerprint and split one backend failure into an issue per page (Sentry
    // WEB-CLIENT-25H / 1JX / 1HM / 27A / 269 were all the same failing
    // `GetPublishedPageExperience`). The explicit fingerprint still pins it.
    Sentry.captureException(wrappedErr, {
      ...safeGQLErrorContext(error, queryKey),
      fingerprint: [
        'graphql-api-server-error',
        String(status),
        operationName ?? 'unknown-operation',
      ],
    });
  } else if (isGraphQLError && error.response.status === 429) {
    // Ahead of the 4xx branch below: should a 429 ever arrive carrying our own
    // error envelope (our origin rate limiting rather than the edge), an
    // `UnknownError` code would otherwise file it as a malformed payload we
    // sent, under the wrong wording and the wrong fingerprint.
    reportRateLimited(error, queryKey);
  } else if (!isGraphQLError) {
    // A plain JS Error — no request payload to worry about.
    Sentry.captureException(error, data);
  } else if (
    // `!isServerError` keeps this off the 5xx path entirely — without it a
    // transient gateway failure coded UNKNOWN_ERROR would report here and
    // defeat the suppression above.
    //
    // A 400 is our own bug: the client validates its inputs, so a payload the
    // server rejects should never have been sent. `UnknownError` joins it
    // because it used to be captured here as the raw ClientError.
    //
    // A 403 joins them when it answered a *mutation*: the app offered an action
    // the server refuses, which is the magic-chat `importChat` shape. A 403 on a
    // query is the routine answer to a navigation and stays out of Sentry.
    //
    // Every other typed 4xx is a legitimate answer and stays out too — a 429 is
    // the one exception, reported by the branch above because the edge answers
    // it without our server ever seeing it. The rest are not unrecorded either:
    // that silence is how an entire class of failures stayed invisible, and the
    // Datadog log below is what covers them now. This is why the ICS-feed
    // mutation's feed-unreachable/forbidden/invalid answers (422, no amount of
    // client validation can confirm a remote feed loads) need no code-specific
    // exemption here: the server answers 422, never 400, so they never reach
    // this branch at all (Sentry WEB-CLIENT-29F).
    // `isGQLErrorOfType` is a type predicate, so its false branch narrows
    // `error` to `never` — it has to come last in this chain.
    !isServerError &&
    (error.response.status === 400 ||
      (error.response.status === 403 && isMutation) ||
      isGQLErrorOfType(error, 'UnknownError'))
  ) {
    reportClientFault(error, queryKey);
  }
  // A transient gateway error matches no branch, so it is intentionally never
  // reported (see TRANSIENT_GATEWAY_STATUSES).

  // Logged for the whole 4xx class, including the 400s captured above: on its
  // own most of this is a legitimate answer, but the rate of it is real
  // observability on whether the client is sending requests it shouldn't.
  if (isGraphQLError && isClientErrorStatus(error.response.status)) {
    logClientError(error, queryKey);
  }

  // A 401 means the session is gone, so put the app into the state an explicit
  // logout would: clear the signed-in user, then let the current route decide
  // whether that means leaving. Previously this only redirected, so on the
  // routes that stay put (`/magic`, `/experience/:id/edit`) the app went on
  // rendering as signed-in off cached data and every write failed on its own.
  if (isGQLError(error) && isGQLErrorOfType(error, 'NotLoggedIn')) {
    // The snippet page handles 401 errors itself so ignore this query
    const snippetQueryKey = createSnippetQueryKey();
    const isSnippetQuery = queryKey && queryKey[0] === snippetQueryKey[0];

    // The profile query expectedly returns 401 if logged out
    const isProfileQuery =
      queryKey && hashKey(queryKey) === hashKey(createProfileQueryKey());

    // The presence of queryHash indicates a query vs mutation
    if (!queryKey || (!isSnippetQuery && !isProfileQuery)) {
      endSession();

      const { shouldRedirect, path } = shouldRedirectOnLogout();
      if (shouldRedirect) {
        window.location.href = path;
      }
    }
  }
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't automatically retry GQL errors
      retry: (_, err) => !isGQLError(err),
    },
    mutations: {
      // Don't automatically retry GQL errors
      retry: (_, err) => !isGQLError(err),
    },
  },
  mutationCache: new MutationCache({
    onError: (error) => errorHandler(error),
  }),
  queryCache: new QueryCache({
    onError: (error, { queryKey }) => errorHandler(error, queryKey),
  }),
});
